/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useMemo, useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";

// ====== API & Utilities ======
import {
  getCurrentCustomer,
  logoutCustomer,
  addStoreReview,
  createTenantOrder,
  getStoreReviews,
  getCustomerOrders,
  cancelCustomerOrder,
  updateTenantOrder,
  getDeliveryByCity
} from "./utils/storeApi";
import { ORDER_STATUS, THEMES, THEME_NAME_TO_ID } from "./theme2/utils/theme2Constants";

// ====== Hooks ======
import { useStoreBootstrap } from "./theme1/hooks/useStoreBootstrap";
import { useDisplayData } from "./theme1/hooks/useDisplayData";
import { n } from "./theme1/utils/theme1Helpers";
import { useSmartCart } from "./hooks/useSmartCart";

// ====== Components ======
import AuthModal from "./components/AuthModal";
import HeaderSection from "./theme2/components/HeaderSection";
import HeroSection from "./theme2/components/HeroSection";
import FiltersSection from "./theme2/components/FiltersSection";
import ProductsSection from "./theme2/components/ProductsSection";
import ReviewsSection from "./theme2/components/ReviewsSection";
import SocialFollowSection from "./theme2/components/SocialFollowSection";
import FooterSection from "./theme2/components/FooterSection";
import WhatsAppButton from "./theme2/components/WhatsAppButton";
import CartSidebar from "./theme2/components/CartSidebar";
import ProductModal from "./theme2/components/ProductModal";
import ReviewModal from "./theme2/components/ReviewModal";
import OrdersModal from "./theme2/components/OrdersModal";
import OrderDetailsPage from "./theme2/components/OrderDetailsPage";
import EditOrderModal from "./theme2/components/EditOrderModal";

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function MakeupStorePage({ tenantId, tenant }) {
  // ====== Bootstrap Data ======
  const {
    loading,
    error,
    storeData,
    apiProducts,
    apiCategories,
    apiCoupons,
    apiBanners,
    apiTags,
    cities,
    storeReviews,
    canReview,
  } = useStoreBootstrap(tenantId);

  const { displayProducts, displayCategories, displayCoupons } = useDisplayData({
    apiProducts,
    apiCategories,
    apiCoupons,
    apiTags
  });

  const { cart, addItem, updateQuantity, removeItem, clearCart, refreshCart } = useSmartCart(tenantId);

  // ====== UI State ======
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(1);

  const resolveThemeId = (themeId, colorName) => {
    if (colorName && THEME_NAME_TO_ID[colorName]) return THEME_NAME_TO_ID[colorName];
    return Number(themeId) || 1;
  };

  useEffect(() => {
    if (tenant?.themeId || tenant?.colorName) {
      setSelectedTheme(resolveThemeId(tenant.themeId, tenant.colorName));
    }
  }, [tenant?.themeId, tenant?.colorName]);

  useEffect(() => {
    if (storeData?.themeId || storeData?.colorName) {
      setSelectedTheme(resolveThemeId(storeData.themeId, storeData.colorName));
    }
  }, [storeData?.themeId, storeData?.colorName]);

  // ====== Auth State ======
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [showAccountMenu, setShowAccountMenu] = useState(false);

  // ====== Orders State ======
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);

  // ====== Reviews State ======
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, description: "" });
  const [liveReviews, setLiveReviews] = useState([]);

  // ====== Coupon State ======
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");

  // ====== Delivery Form State ======
  const [deliveryForm, setDeliveryForm] = useState({
    name: "",
    phone: "",
    cityId: localStorage.getItem("selectedCityId") || "",
    address: "",
    email: ""
  });

  const [pendingCartItem, setPendingCartItem] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const asText = (value) => (value === null || value === undefined ? "" : String(value).trim());
  const asNumber = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };

  // ====== Other State ======
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ============================================================
  // EFFECTS
  // ============================================================

  useEffect(() => {
    const savedCustomer = JSON.parse(localStorage.getItem("storeCustomer"));
    if (savedCustomer) {
      setDeliveryForm(prev => ({
        ...prev,
        name: savedCustomer.name || "",
        phone: savedCustomer.phone || "",
        address: savedCustomer.address || "",
        email: savedCustomer.email || "",
        cityId: savedCustomer.cityId || prev.cityId || "",
      }));
    }
  }, []);

  useEffect(() => {
    if (displayProducts?.length) {
      window.__STORE_PRODUCTS__ = displayProducts;
      refreshCart();
    }
  }, [displayProducts, refreshCart]);

  useEffect(() => {
    const customer = getCurrentCustomer();
    if (customer) {
      setIsLoggedIn(true);
      setCustomerName(customer.name || "عميل");
      setDeliveryForm({
        name: customer.name || "",
        phone: customer.phone || "",
        address: customer.address || "",
        email: customer.email || "",
        cityId: customer.cityId || deliveryForm.cityId || "",
      });
    }
  }, [isLoggedIn]);

  // Persist city selection
  useEffect(() => {
    if (deliveryForm.cityId) {
      localStorage.setItem("selectedCityId", deliveryForm.cityId);
    }
  }, [deliveryForm.cityId]);

  useEffect(() => {
    if (deliveryForm.cityId && tenantId) {
      getDeliveryByCity(tenantId, deliveryForm.cityId).then(res => {
        if (res) setDeliveryInfo(res);
        else setDeliveryInfo(null);
      });
    } else {
      setDeliveryInfo(null);
    }
  }, [deliveryForm.cityId, tenantId]);

  // Update live reviews when store reviews change
  useEffect(() => {
    setLiveReviews(storeReviews?.filter(r => r.isActive === 1) || []);
  }, [storeReviews]);

  // Refresh reviews after closing review modal
  useEffect(() => {
    if (!showReviewModal) {
      getStoreReviews(tenantId);
    }
  }, [showReviewModal, tenantId]);

  // Build variants list
  const allVariants = useMemo(() => {
    if (!apiProducts?.length) return [];
    const all = [];

    apiProducts.forEach(p => {
      (p.variants || []).forEach(v => {
        all.push({
          itemId: p.id,
          itemName: p.name || "",
          description: p.description || "",
          discountValue: p.discountValue || 0,
          discountType: p.discountType || 0,
          colorHex: v.color?.hex || v.colorHex || "",
          variantId: Number(v.variantId),
          basePrice: Number(v.basePrice || v.price || 0),
          finalPrice: Number(v.finalPrice || v.price || v.basePrice || 0),
          size: v.size || (v.options?.[0]?.label) || "",
          imageUrl: v.imageUrl || p.mainImage || p.mainImageUrl,
          stockQty: Number(v.stockQty || 0)
        });
      });
    });
    return all;
  }, [apiProducts]);

  // ============================================================
  // COMPUTED VALUES
  // ============================================================

  const theme = THEMES[selectedTheme] || THEMES[1];
  const customer = isLoggedIn ? getCurrentCustomer() : null;

  const filteredProducts = useMemo(() => {
    let data = [...displayProducts];

    if (selectedCategory && selectedCategory !== 0) {
      data = data.filter(p => Number(p.categoryId) === Number(selectedCategory));
    }

    if (selectedSubCategory !== null && selectedSubCategory !== undefined) {
      data = data.filter(p => Number(p.subCategoryId) === Number(selectedSubCategory));
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(p =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.brand || "").toLowerCase().includes(q) ||
        (p.categoryName || "").toLowerCase().includes(q)
      );
    }

    // Sorting
    if (sortBy === "price-low") {
      data.sort((a, b) => n(a.variants?.[0]?.finalPrice) - n(b.variants?.[0]?.finalPrice));
    } else if (sortBy === "price-high") {
      data.sort((a, b) => n(b.variants?.[0]?.finalPrice) - n(a.variants?.[0]?.finalPrice));
    } else if (sortBy === "newest") {
      data = data.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)).slice(0, 20);
    } else if (sortBy === "alphabetical") {
      data.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortBy === "big_discount") {
      data.sort((a, b) => n(b.variants?.[0]?.discountValue) - n(a.variants?.[0]?.discountValue));
    }

    return data;
  }, [displayProducts, selectedCategory, selectedSubCategory, search, sortBy]);

  const subtotal = useMemo(() => {
    return cart.reduce(
      (s, i) => s + Number(i.price || i.unitPrice || 0) * Number(i.quantity || i.qty || 1),
      0
    );
  }, [cart]);

  const couponDiscount = useMemo(() => {
    if (!appliedCoupon) return 0;
    return Math.min(appliedCoupon.discountValue || 0, subtotal);
  }, [appliedCoupon, subtotal]);

  const shipping = useMemo(() => {
    if (!deliveryInfo) return 0;
    return Number(deliveryInfo.deliveryCompanyFees || 0);
  }, [deliveryInfo]);

  const total = subtotal - couponDiscount + shipping;

  // ============================================================
  // HANDLERS
  // ============================================================

  const handleApplyCoupon = (e) => {
    e.preventDefault();

    if (!couponCode.trim()) return;

    const coupon = displayCoupons.find(
      c => c.code === couponCode.trim().toUpperCase()
    );

    if (!coupon) {
      setCouponError("الكوبون غير صالح");
      return;
    }

    setAppliedCoupon(coupon);
    localStorage.setItem("storeCoupon", JSON.stringify(coupon));
    setCouponCode("");
    setCouponError("");
  };

  const handleCheckoutSubmit = async () => {
    if (cart.length === 0) {
      Swal.fire({
        icon: "error",
        title: "السلة فارغة",
        text: "أضف منتجاً إلى السلة أولاً"
      });
      return;
    }

    if (!deliveryForm.cityId || !Number.isFinite(Number(deliveryForm.cityId))) {
      Swal.fire({ icon: "error", title: "يرجى اختيار المحافظة" });
      return;
    }

    const customer = getCurrentCustomer();
    if (!customer?.customerId) {
      setShowAuthModal(true);
      return;
    }

    const city = cities.find(c => Number(c.cityId) === Number(deliveryForm.cityId));
    if (!city) {
      Swal.fire({ icon: "error", title: "المحافظة غير صحيحة" });
      return;
    }

    const orderObj = {
      orderId: localStorage.getItem("currentOrderId"),
      items: cart.map(item => ({
        itemId: asNumber(item.itemId, 0),
        variantId: asNumber(item.variantId, 0),
        itemName: asText(item.name),
        variantName: asText(item.variantName),
        colorName: asText(item.color),
        size: asText(item.size),
        quantity: asNumber(item.quantity, 0),
        price: asNumber(item.price, 0),
        imageUrl: asText(item.imageUrl),
        stockQty: asNumber(item.stockQty, 0),
        discount: asNumber(item.discount, 0),
        lineTotal: asNumber(item.price, 0) * asNumber(item.quantity, 0)
      })),
      totalAmount: asNumber(subtotal, 0),
      netAmount: asNumber(total, 0),
      discountAmount: asNumber(couponDiscount, 0),
      deliveryId: Number(deliveryInfo?.deliveryId || 0)
    };

    const finalCustomerName = asText(deliveryForm.name) || asText(customer?.name);
    const finalCustomerPhone = asText(deliveryForm.phone) || asText(customer?.phone);
    if (!finalCustomerName || !finalCustomerPhone) {
      Swal.fire({ icon: "error", title: "يرجى إدخال الاسم ورقم الهاتف" });
      return;
    }

    const updatedCustomer = {
      ...customer,
      name: finalCustomerName,
      phone: finalCustomerPhone,
      address: asText(deliveryForm.address) || asText(customer?.address),
      email: asText(deliveryForm.email) || asText(customer?.email),
      cityId: asNumber(deliveryForm.cityId, 0)
    };

    localStorage.setItem("storeCustomer", JSON.stringify(updatedCustomer));
    const storedCoupon = (() => {
      try { return JSON.parse(localStorage.getItem("storeCoupon") || "null"); }
      catch { return null; }
    })();

    try {
      setIsSubmitting(true);
      const result = await createTenantOrder(tenantId, city, orderObj, storedCoupon);

      if (result && result.success === true) {
        localStorage.setItem("lastOrderId", orderObj.orderId);
        localStorage.removeItem("storeCoupon");
        setAppliedCoupon(null);
        setCouponError("");

        clearCart();
        await refreshCart();

        Swal.fire({
          icon: "success",
          title: "تم إرسال الطلب بنجاح",
          text: "سيتم التواصل معك قريباً لتوصيل الطلب",
          timer: 2500,
          showConfirmButton: false
        });

        setCartOpen(false);
      } else {
        Swal.fire({ icon: "error", title: "فشل إرسال الطلب" });
      }
    } catch (err) {
      console.error("Checkout Error:", err);
      Swal.fire({ icon: "error", title: "خطأ تقني أثناء إرسال الطلب" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadCustomerOrders = async () => {
    const customerId = localStorage.getItem("storeCustomerId");
    if (!customerId) return;

    const res = await getCustomerOrders(tenantId, customerId);
    if (res?.success) {
      setCustomerOrders(res.data || []);
    }
  };

  const canEditOrDelete = (order) =>
    Number(order.statusId) === ORDER_STATUS.PENDING;

  const handleDeleteOrder = async (order) => {
    const confirm = await Swal.fire({
      title: "تأكيد الحذف",
      text: "هل تريد حذف هذا الطلب نهائياً؟",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "نعم احذف",
      cancelButtonText: "إلغاء"
    });

    if (!confirm.isConfirmed) return;

    const res = await cancelCustomerOrder(
      tenantId,
      order.orderId,
      order.statusId
    );

    if (res?.success) {
      setCustomerOrders(prev =>
        prev.filter(o => o.orderId !== order.orderId)
      );
      setSelectedOrderDetails(null);
      Swal.fire("تم", "تم حذف الطلب بنجاح", "success");
    } else {
      Swal.fire("خطأ", res?.error || "فشل حذف الطلب", "error");
    }
  };

  const saveEditedOrder = async (order) => {
    // Validate
    if (!order?.items || order.items.length === 0) {
      Swal.fire("خطأ", "الطلب فارغ", "error");
      return;
    }

    const city = cities.find(
      c => Number(c.cityId) === Number(order.cityId)
    );

    if (!city) {
      Swal.fire("خطأ", "المحافظة غير صحيحة", "error");
      return;
    }

    // Normalize order
    const normalizedOrder = {
      ...order,
      items: order.items.map(d => ({
        itemId: asNumber(d.itemId, 0),
        variantId: asNumber(d.variantId, 0),
        itemName: asText(d.itemName),
        variantName: asText(d.variantName),
        colorName: asText(d.colorName),
        size: asText(d.size),
        quantity: asNumber(d.quantity, 0),
        price: asNumber(d.price, 0),
        imageUrl: asText(d.imageUrl),
        stockQty: asNumber(d.stockQty || 0, 0),
        discount: 0,
        lineTotal: asNumber(d.quantity, 0) * asNumber(d.price, 0)
      }))
    };

    const res = await updateTenantOrder(tenantId, normalizedOrder, city);

    if (res.success) {
      const savedOrder = res.data?.order || res.data?.data || res.data || normalizedOrder;
      setCustomerOrders(prev =>
        prev.map(o =>
          o.orderId === order.orderId
            ? { ...o, ...savedOrder }
            : o
        )
      );

      setEditingOrder(null);
      Swal.fire("تم", "تم حفظ التعديلات بنجاح", "success");
    } else {
      Swal.fire("خطأ", res.error || "فشل حفظ التعديلات", "error");
    }
  };

  const handleAddToCart = async (data, qtyToAdd = 1) => {
    if (!localStorage.getItem("storeCustomerId")) {
      setPendingCartItem({ ...data, quantity: qtyToAdd });
      setShowAuthModal(true);
      return;
    }

    const res = await addItem(data, qtyToAdd);

    if (res?.success) {
      refreshCart();
    } else {
      Swal.fire("تنبيه", res?.error || "فشل إضافة المنتج", "warning");
    }
  };

  const handleUpdateQty = async (variantId, delta) => {
    const res = await updateQuantity(variantId, delta);
    if (!res.success && res.error) {
      Swal.fire("تنبيه", res.error, "warning");
    } else {
      await refreshCart();
    }
  };

  const handleLogout = () => {
    logoutCustomer();
    localStorage.removeItem("storeCustomer");
    localStorage.removeItem("storeCustomerId");
    localStorage.removeItem("storeCustomerToken");
    setDeliveryForm({
      name: "",
      phone: "",
      address: "",
      email: "",
      cityId: localStorage.getItem("selectedCityId") || ""
    });
    clearCart();
    setIsLoggedIn(false);
    Swal.fire({
      icon: 'success',
      title: 'تم تسجيل الخروج بنجاح',
    });
  };

  // ============================================================
  // LOADING & ERROR STATES
  // ============================================================

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div dir="rtl" className={`min-h-screen bg-gradient-to-br ${theme.bgGradient}`}>
      {/* ====== Header ====== */}
      <HeaderSection
        storeData={storeData}
        theme={theme}
        selectedTheme={selectedTheme}
        setSelectedTheme={setSelectedTheme}
        isLoggedIn={isLoggedIn}
        customer={customer}
        showAccountMenu={showAccountMenu}
        setShowAccountMenu={setShowAccountMenu}
        loadCustomerOrders={loadCustomerOrders}
        setShowOrdersModal={setShowOrdersModal}
        setShowAuthModal={setShowAuthModal}
        handleLogout={handleLogout}
        cartItems={cart}
        setCartOpen={setCartOpen}
      />

      {/* ====== Hero Section ====== */}
      <HeroSection
        storeData={storeData}
        apiProducts={apiProducts}
        apiCategories={apiCategories}
        displayBanners={apiBanners}
        storeReviews={liveReviews}
        theme={theme}
      />

      {/* ====== Filters ====== */}
      <FiltersSection
        theme={theme}
        displayCategories={displayCategories}
        selectedCategory={selectedCategory}
        selectedSubCategory={selectedSubCategory}
        handleCategoryClick={(catId, subId = null) => {
          setSelectedCategory(Number(catId));
          setSelectedSubCategory(subId ? Number(subId) : null);
        }}
        search={search}
        setSearch={setSearch}
        sortBy={sortBy}
        setSortBy={setSortBy}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />

      {/* ====== Products Grid ====== */}
      <ProductsSection
        theme={theme}
        filteredProducts={filteredProducts}
        viewMode={viewMode}
        setViewMode={setViewMode}
        setSelected={(p) => setSelectedProduct(p)}
        handleAddToCart={handleAddToCart}
      />

      {/* ====== Product Modal ====== */}
      <ProductModal
        selected={selectedProduct}
        setSelected={setSelectedProduct}
        products={displayProducts}
        theme={theme}
        handleAddToCart={handleAddToCart}
      />

      {/* ====== Reviews Section ====== */}
      <ReviewsSection storeReviews={liveReviews} theme={theme} />

      {/* ====== Social Follow ====== */}
      <SocialFollowSection storeData={storeData} theme={theme} />

      {/* ====== Footer ====== */}
      <FooterSection
        storeName={storeData.storeName}
        policies={storeData.policies}
      />

      {/* ====== WhatsApp Button ====== */}
      <WhatsAppButton storeData={storeData} />

      {/* ====== Cart Sidebar ====== */}
      <CartSidebar
        theme={theme}
        cartOpen={cartOpen}
        setCartOpen={setCartOpen}
        cartItems={cart}
        updateQtyAPI={handleUpdateQty}
        removeFromCartAPI={removeItem}
        appliedCoupon={appliedCoupon}
        couponDiscount={couponDiscount}
        shipping={shipping}
        couponCode={couponCode}
        setCouponCode={setCouponCode}
        couponError={couponError}
        handleApplyCoupon={handleApplyCoupon}
        displayCoupons={displayCoupons}
        deliveryForm={deliveryForm}
        setDeliveryForm={setDeliveryForm}
        cities={cities}
        handleCheckoutSubmit={handleCheckoutSubmit}
        setShowAuthModal={setShowAuthModal}
        subtotal={subtotal}
        total={total}
      />

      {/* ====== Review Modal ====== */}
      {showReviewModal && (
        <ReviewModal
          show={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          tenantId={tenantId}
          newReview={newReview}
          setNewReview={setNewReview}
          addStoreReview={addStoreReview}
          getStoreReviews={getStoreReviews}
          setStoreReviews={setLiveReviews}
          theme={theme}
        />
      )}

      {/* ====== Auth Modal ====== */}
      <AuthModal
        isOpen={showAuthModal}
        tenantId={tenantId}
        onClose={() => {
          setShowAuthModal(false);
          setPendingCartItem(null);
        }}
        onSuccess={(c) => {
          setIsLoggedIn(true);
          setCustomerName(c.name || "عميل");
          if (pendingCartItem) {
            handleAddToCart(pendingCartItem, pendingCartItem.quantity);
            setPendingCartItem(null);
          }
        }}
      />

      {/* ====== Orders List Modal ====== */}
      {showOrdersModal && !selectedOrderDetails && (
        <OrdersModal
          show={showOrdersModal}
          theme={theme}
          orders={customerOrders}
          onClose={() => setShowOrdersModal(false)}
          openOrderDetails={(order) => {
            setSelectedOrderDetails(order);
            setShowOrdersModal(false);
          }}
        />
      )}

      {/* ====== Order Details Page ====== */}
      {selectedOrderDetails && (
        <OrderDetailsPage
          order={selectedOrderDetails}
          theme={theme}
          onClose={() => setSelectedOrderDetails(null)}
          onEdit={(order) => {
            setEditingOrder(JSON.parse(JSON.stringify(order)));
            setSelectedOrderDetails(null);
          }}
          onDelete={handleDeleteOrder}
        />
      )}

      {/* ====== Edit Order Modal ====== */}
      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          products={apiProducts}
          theme={theme}
          onCancel={() => setEditingOrder(null)}
          onSave={saveEditedOrder}
        />
      )}
    </div>
  );
}
